import React, { useState, useEffect, useCallback, useRef } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import toast from 'react-hot-toast';
import { tasksAPI } from '../../services/api';

// ── Column definitions ─────────────────────────────────────────────────────────
const COLUMNS = [
  { status: 'To Do',       color: '#64748b', accent: '#f8fafc', border: '#e2e8f0', dot: '#94a3b8' },
  { status: 'In Progress', color: '#d97706', accent: '#fffbeb', border: '#fde68a', dot: '#f59e0b' },
  { status: 'Review',      color: '#7c3aed', accent: '#faf5ff', border: '#ddd6fe', dot: '#8b5cf6' },
  { status: 'Done',        color: '#16a34a', accent: '#f0fdf4', border: '#bbf7d0', dot: '#22c55e' },
];

const PRIORITY_COLORS = {
  Critical: { bg: '#fee2e2', text: '#dc2626' },
  High:     { bg: '#ffedd5', text: '#ea580c' },
  Medium:   { bg: '#dbeafe', text: '#2563eb' },
  Low:      { bg: '#f1f5f9', text: '#64748b' },
};

// ── Main Kanban Component ─────────────────────────────────────────────────────
export default function TaskKanban({ filters = {}, onTaskClick }) {
  const [columns, setColumns]   = useState({
    'To Do': [], 'In Progress': [], 'Review': [], 'Done': [],
  });
  const [loading, setLoading]   = useState(
    Object.fromEntries(COLUMNS.map(c => [c.status, true]))
  );
  // Keep a ref so drag handlers can read current columns without stale closure
  const columnsRef = useRef(columns);
  useEffect(() => { columnsRef.current = columns; }, [columns]);

  // ── Fetch one column ────────────────────────────────────────────────────────
  const fetchColumn = useCallback(async (status) => {
    setLoading(prev => ({ ...prev, [status]: true }));
    try {
      const params = {
        status,
        limit: 100,
        ...(filters.priority   && { priority:    filters.priority }),
        ...(filters.category   && { category:    filters.category }),
        ...(filters.assignedTo && { assigned_to: filters.assignedTo }),
        ...(filters.search     && { search:      filters.search }),
      };

      let tasks;
      if (filters.quick === 'my') {
        const res = await tasksAPI.getMyTasks(params);
        const all = res.data?.data?.tasks || [];
        tasks = all.filter(t => t.status === status);
      } else {
        const res = await tasksAPI.getTasks(params);
        tasks = res.data?.data?.tasks || [];
      }

      setColumns(prev => ({ ...prev, [status]: tasks }));
    } catch {
      // fail silently per column
    } finally {
      setLoading(prev => ({ ...prev, [status]: false }));
    }
  }, [filters]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Re-fetch all columns whenever filters change
  useEffect(() => {
    COLUMNS.forEach(col => fetchColumn(col.status));
  }, [fetchColumn]);

  // ── Drag end handler ────────────────────────────────────────────────────────
  const handleDragEnd = async (result) => {
    const { source, destination, draggableId } = result;
    if (!destination) return;

    const srcStatus = source.droppableId;
    const dstStatus = destination.droppableId;

    // Same position — no-op
    if (srcStatus === dstStatus && source.index === destination.index) return;

    // Snapshot current state for potential revert
    const snapshot = { ...columnsRef.current };
    const movedTask = snapshot[srcStatus][source.index];

    // Optimistic update
    setColumns(prev => {
      const src = Array.from(prev[srcStatus]);
      if (srcStatus === dstStatus) {
        // Reorder within same column
        const [moved] = src.splice(source.index, 1);
        src.splice(destination.index, 0, moved);
        return { ...prev, [srcStatus]: src };
      }
      // Move between columns
      const dst = Array.from(prev[dstStatus]);
      const [moved] = src.splice(source.index, 1);
      dst.splice(destination.index, 0, { ...moved, status: dstStatus });
      return { ...prev, [srcStatus]: src, [dstStatus]: dst };
    });

    // Only hit the API when the column actually changes
    if (srcStatus !== dstStatus) {
      try {
        await tasksAPI.updateStatus(draggableId, dstStatus);
        toast.success(`"${movedTask?.title}" → ${dstStatus}`);
      } catch (err) {
        toast.error(err?.response?.data?.message || 'Failed to update status');
        // Revert both affected columns by re-fetching
        fetchColumn(srcStatus);
        fetchColumn(dstStatus);
      }
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      {/* Outer scroll container — enables mobile horizontal swipe */}
      <div
        style={{
          display: 'flex',
          gap: 12,
          overflowX: 'auto',
          paddingBottom: 16,
          minHeight: 400,
          WebkitOverflowScrolling: 'touch',
          scrollSnapType: 'x proximity',
        }}
      >
        {COLUMNS.map(col => (
          <KanbanColumn
            key={col.status}
            col={col}
            tasks={columns[col.status] || []}
            isLoading={loading[col.status]}
            onTaskClick={onTaskClick}
          />
        ))}
      </div>
    </DragDropContext>
  );
}

// ── Column ────────────────────────────────────────────────────────────────────
function KanbanColumn({ col, tasks, isLoading, onTaskClick }) {
  return (
    <div
      style={{
        flex: '0 0 280px',
        minWidth: 280,
        maxWidth: 320,
        background: col.accent,
        borderRadius: 12,
        border: `1.5px solid ${col.border}`,
        display: 'flex',
        flexDirection: 'column',
        maxHeight: 'calc(100vh - 300px)',
        scrollSnapAlign: 'start',
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '11px 14px',
          borderBottom: `2px solid ${col.color}22`,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          borderRadius: '10px 10px 0 0',
          flexShrink: 0,
        }}
      >
        <span
          style={{
            width: 10, height: 10, borderRadius: '50%',
            background: col.dot, flexShrink: 0,
            boxShadow: `0 0 0 3px ${col.color}22`,
          }}
        />
        <span style={{ fontWeight: 700, fontSize: 13, color: '#1e293b', flex: 1, userSelect: 'none' }}>
          {col.status}
        </span>
        <span
          style={{
            background: col.color, color: '#fff',
            borderRadius: 12, padding: '2px 9px',
            fontSize: 11, fontWeight: 700, minWidth: 24, textAlign: 'center',
          }}
        >
          {isLoading ? '…' : tasks.length}
        </span>
      </div>

      {/* Droppable area */}
      <Droppable droppableId={col.status}>
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            style={{
              flex: 1,
              overflowY: 'auto',
              padding: '8px',
              background: snapshot.isDraggingOver ? `${col.color}12` : 'transparent',
              transition: 'background 0.15s',
              minHeight: 64,
              borderRadius: '0 0 10px 10px',
            }}
          >
            {isLoading ? (
              <ColumnSkeleton />
            ) : tasks.length === 0 ? (
              <EmptyColumn />
            ) : (
              tasks.map((task, index) => (
                <TaskCard
                  key={task._id}
                  task={task}
                  index={index}
                  onTaskClick={onTaskClick}
                />
              ))
            )}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </div>
  );
}

// ── Task Card ─────────────────────────────────────────────────────────────────
function TaskCard({ task, index, onTaskClick }) {
  const pColors   = PRIORITY_COLORS[task.priority] || PRIORITY_COLORS.Low;
  const due       = task.due_date ? new Date(task.due_date) : null;
  const now       = new Date();
  const isOverdue = due && due < now && due.toDateString() !== now.toDateString();
  const doneCount = (task.subtasks || []).filter(s => s.status === 'Done').length;
  const totalSubs = (task.subtasks || []).length;
  const pct       = task.completion_percentage ?? 0;

  return (
    <Draggable draggableId={task._id} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          style={{
            background: '#fff',
            borderRadius: 9,
            padding: '10px 12px',
            marginBottom: 8,
            boxShadow: snapshot.isDragging
              ? '0 10px 30px rgba(0,0,0,0.18)'
              : '0 1px 3px rgba(0,0,0,0.07)',
            border: snapshot.isDragging ? '1.5px solid #1B2A6B' : '1px solid #f1f5f9',
            cursor: 'grab',
            userSelect: 'none',
            opacity: snapshot.isDragging ? 0.95 : 1,
            ...provided.draggableProps.style,
          }}
        >
          {/* Row 1: task number + priority badge */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 5 }}>
            <span
              style={{
                fontSize: 10, color: '#94a3b8',
                fontFamily: 'monospace', letterSpacing: '-0.02em', flex: 1,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}
            >
              {task.task_number}
            </span>
            <span
              style={{
                background: pColors.bg, color: pColors.text,
                borderRadius: 4, padding: '1px 7px', fontSize: 10, fontWeight: 700, flexShrink: 0,
              }}
            >
              {task.priority}
            </span>
          </div>

          {/* Row 2: title (clickable) */}
          <div
            role="button"
            tabIndex={0}
            onClick={() => onTaskClick?.(task._id)}
            onKeyDown={e => e.key === 'Enter' && onTaskClick?.(task._id)}
            style={{
              fontSize: 13, fontWeight: 600, color: '#1e293b',
              lineHeight: 1.4, marginBottom: 8,
              cursor: 'pointer',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {task.title}
          </div>

          {/* Progress bar (if any) */}
          {pct > 0 && (
            <div style={{ height: 3, background: '#f1f5f9', borderRadius: 2, marginBottom: 7, overflow: 'hidden' }}>
              <div
                style={{
                  height: '100%', width: `${pct}%`,
                  background: pct === 100 ? '#22c55e' : '#1B2A6B',
                  borderRadius: 2,
                }}
              />
            </div>
          )}

          {/* Row 3: assignee avatar + due date + subtask count */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
            {/* Assignee avatar */}
            {task.assigned_to && (
              <div
                title={task.assigned_to?.name}
                style={{
                  width: 22, height: 22, borderRadius: '50%',
                  background: '#1B2A6B', color: '#C9A84C',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 700, fontSize: 10, flexShrink: 0,
                }}
              >
                {task.assigned_to?.name?.[0]?.toUpperCase() || '?'}
              </div>
            )}

            {/* Due date */}
            {due && (
              <span
                style={{
                  fontSize: 10,
                  color: isOverdue ? '#dc2626' : '#64748b',
                  background: isOverdue ? '#fee2e2' : '#f1f5f9',
                  borderRadius: 4, padding: '1px 5px',
                  fontWeight: isOverdue ? 700 : 400,
                  whiteSpace: 'nowrap',
                }}
              >
                📅 {due.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
              </span>
            )}

            {/* Subtask count */}
            {totalSubs > 0 && (
              <span
                style={{
                  marginLeft: 'auto',
                  fontSize: 10, color: '#64748b',
                  background: '#f8fafc', borderRadius: 4, padding: '1px 5px',
                  whiteSpace: 'nowrap',
                }}
              >
                ✅ {doneCount}/{totalSubs}
              </span>
            )}
          </div>
        </div>
      )}
    </Draggable>
  );
}

// ── Column states ─────────────────────────────────────────────────────────────
function ColumnSkeleton() {
  return (
    <div>
      {[1, 2].map(i => (
        <div
          key={i}
          style={{
            background: 'rgba(0,0,0,0.05)', borderRadius: 8, height: 80,
            marginBottom: 8, animation: 'pulse 1.5s ease-in-out infinite',
          }}
        />
      ))}
    </div>
  );
}

function EmptyColumn() {
  return (
    <div
      style={{
        textAlign: 'center', padding: '28px 12px',
        color: '#cbd5e1', fontSize: 12,
      }}
    >
      <div style={{ fontSize: 24, marginBottom: 6 }}>📭</div>
      No tasks here
    </div>
  );
}
